class CartDrawerController {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.toggleNodes = [...document.querySelectorAll('[data-cart-toggle]')];
    this.countNodes = [...document.querySelectorAll('[data-cart-count]')];
    this.lastActiveElement = null;
    this.pendingItemKeys = new Set();
    this.isRefreshing = false;
    this.timerInterval = null;

    this.cacheNodes();
    this.bindUI();
    this.initTimer();
    this.syncEmptyState();
    this.updateGlobalCount();
    this.removeOrphanProtection();
  }

  get rootUrl() {
    return window.Shopify?.routes?.root || '/';
  }

  cacheNodes() {
    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.body = this.drawer.querySelector('[data-cart-drawer-body]');
    this.footer = this.drawer.querySelector('[data-cart-drawer-footer]');
    this.liveRegion = this.drawer.querySelector('[data-cart-drawer-live-region]');
    this.loadingLabel = this.drawer.dataset.cartDrawerLoadingLabel || 'Loading cart';
  }

  bindUI() {
    this.toggleNodes.forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');

      trigger.addEventListener('click', async (event) => {
        event.preventDefault();
        this.lastActiveElement = trigger;
        await this.refresh();
        this.open();
      });
    });

    document.addEventListener('submit', (event) => this.handleAddToCart(event));
    document.addEventListener('keydown', (event) => this.handleKeydown(event));
    this.drawer.addEventListener('click', (event) => this.handleDrawerClick(event));
    this.drawer.addEventListener('change', (event) => this.handleDrawerChange(event));
  }

  async handleAddToCart(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-product-form]')) return;

    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'add') return;

    event.preventDefault();

    const formData = new FormData(form);

    try {
      submitter.disabled = true;

      const response = await fetch(`${this.rootUrl}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(
          data?.description ||
          data?.message ||
          'Unable to add product'
        );
      }

      await this.refresh();
      this.open();

    } catch (error) {
      console.error(error);

      const errorElement = form.querySelector('[data-add-to-cart-error]');

      if (errorElement) {
        errorElement.textContent = error?.message || '';
        errorElement.hidden = false;
      } else {
        window.location.href = form.action || '/cart';
      }

    } finally {
      submitter.disabled = false;
    }
  }

  async handleDrawerClick(event) {
    const closeTrigger = event.target.closest('[data-cart-drawer-close]');

    if (closeTrigger) {
      event.preventDefault();
      this.close();
      return;
    }

    const quantityTrigger = event.target.closest('[data-cart-quantity-change]');

    if (quantityTrigger) {
      const item = quantityTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;

      if (!key || this.pendingItemKeys.has(key)) return;

      const delta = Number(quantityTrigger.dataset.cartQuantityDelta || 0);
      const input = item.querySelector('[data-cart-quantity-input]');
      const current = Number(input?.value || 0);
      const next = Math.max(0, current + delta);

      await this.changeItem(key, next);
      return;
    }

    const removeTrigger = event.target.closest('[data-cart-remove]');

    if (removeTrigger) {
      const item = removeTrigger.closest('[data-cart-item-key]');
      const key = item?.dataset.cartItemKey;

      if (key) {
        await this.changeItem(key, 0);
      }
    }
  }

  async handleDrawerChange(event) {
    const protectionToggle = event.target.closest(
      '[data-shipping-protection-toggle]'
    );

    if (protectionToggle) {
      await this.changeShippingProtection(
        protectionToggle.checked,
        protectionToggle
      );
      return;
    }

    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;

    const item = input.closest('[data-cart-item-key]');
    const key = item?.dataset.cartItemKey;

    if (!key || this.pendingItemKeys.has(key)) return;

    const quantity = Math.max(0, Number(input.value) || 0);

    await this.changeItem(key, quantity);
  }

  async changeItem(key, quantity) {
    if (!key || this.pendingItemKeys.has(key)) return;

    this.pendingItemKeys.add(key);
    this.setItemPendingState(key, true);

    try {
      const response = await fetch(`${this.rootUrl}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity
        })
      });

      if (!response.ok) {
        throw new Error('Unable to update cart item');
      }

      await response.json();

      await this.refresh({
        normalizeProtection: true
      });

    } catch (error) {
      console.error(error);
      await this.refresh();

    } finally {
      this.pendingItemKeys.delete(key);
      this.setItemPendingState(key, false);
    }
  }

  async changeShippingProtection(shouldAdd, toggle) {
    const merchandiseCount = Number(
      this.drawer.dataset.cartMerchandiseCount || 0
    );

    const variantId = Number(
      this.drawer.dataset.cartProtectionVariant || 0
    );

    const protectionKey =
      this.drawer.dataset.cartProtectionKey || '';

    const protectionAvailable =
      this.drawer.dataset.cartProtectionAvailable === 'true';

    // Shipping Protection mag nooit zelfstandig in de cart staan.
    if (merchandiseCount <= 0) {
      toggle.checked = false;
      return;
    }

    toggle.disabled = true;

    try {
      if (shouldAdd) {
        if (!variantId) {
          throw new Error(
            'Shipping Protection product is not configured.'
          );
        }

        if (!protectionAvailable) {
          throw new Error(
            'Shipping Protection variant is not available for purchase.'
          );
        }

        if (!protectionKey) {
          const formData = new FormData();

          formData.append('id', String(variantId));
          formData.append('quantity', '1');
          formData.append(
            'properties[_shipping_protection]',
            'true'
          );

          const response = await fetch(
            `${this.rootUrl}cart/add.js`,
            {
              method: 'POST',
              headers: {
                Accept: 'application/json'
              },
              body: formData
            }
          );

          if (!response.ok) {
            const data = await response.json().catch(() => null);

            throw new Error(
              data?.description ||
              data?.message ||
              'Unable to add Shipping Protection'
            );
          }
        }

      } else if (protectionKey) {
        const response = await fetch(
          `${this.rootUrl}cart/change.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              id: protectionKey,
              quantity: 0
            })
          }
        );

        if (!response.ok) {
          const data = await response.json().catch(() => null);

          throw new Error(
            data?.description ||
            data?.message ||
            'Unable to remove Shipping Protection'
          );
        }
      }

      await this.refresh();

    } catch (error) {
      console.error(error);

      toggle.checked = !shouldAdd;

      alert(
        error?.message ||
        'Unable to update Shipping Protection'
      );

    } finally {
      toggle.disabled = false;
    }
  }

  async refresh({ normalizeProtection = false } = {}) {
    if (this.isRefreshing) return;

    this.isRefreshing = true;

    const wasOpen =
      this.drawer.classList.contains('is-open');

    this.showSkeleton();

    const fetchFreshDrawer = async () => {
      const url =
        `${this.rootUrl}cart?drawer_refresh=${Date.now()}`;

      const response = await fetch(url, {
        headers: {
          Accept: 'text/html'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(
          'Unable to refresh cart drawer'
        );
      }

      const html = await response.text();

      const doc = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const freshDrawer =
        doc.querySelector('[data-cart-drawer]');

      if (!freshDrawer) {
        throw new Error(
          'Fresh cart drawer markup was not found'
        );
      }

      return freshDrawer;
    };

    try {
      let freshDrawer = await fetchFreshDrawer();

      // Als het laatste echte product verwijderd is,
      // Shipping Protection eerst verwijderen.
      if (normalizeProtection) {
        const merchandiseCount = Number(
          freshDrawer.dataset.cartMerchandiseCount || 0
        );

        const protectionKey =
          freshDrawer.dataset.cartProtectionKey || '';

        if (
          merchandiseCount <= 0 &&
          protectionKey
        ) {
          const response = await fetch(
            `${this.rootUrl}cart/change.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
              },
              body: JSON.stringify({
                id: protectionKey,
                quantity: 0
              })
            }
          );

          if (response.ok) {
            freshDrawer = await fetchFreshDrawer();
          }
        }
      }

      this.replaceDrawerMarkup(
        freshDrawer,
        wasOpen
      );

      this.syncEmptyState();
      this.updateGlobalCount();
      this.initTimer();

    } catch (error) {
      console.error(error);

    } finally {
      this.isRefreshing = false;
    }
  }

  syncEmptyState() {
    const merchandiseCount = Number(
      this.drawer.dataset.cartMerchandiseCount || 0
    );

    const footer =
      this.drawer.querySelector(
        '[data-cart-drawer-footer]'
      );

    if (footer) {
      footer.hidden = merchandiseCount <= 0;
      footer.style.display =
        merchandiseCount <= 0 ? 'none' : '';
    }

    const protectionToggle =
      this.drawer.querySelector(
        '[data-shipping-protection-toggle]'
      );

    if (
      protectionToggle &&
      merchandiseCount <= 0
    ) {
      protectionToggle.checked = false;
      protectionToggle.disabled = true;
    }
  }

  replaceDrawerMarkup(freshDrawer, wasOpen) {
    const preservedClass =
      wasOpen
        ? 'cart-drawer is-open'
        : 'cart-drawer';

    [...this.drawer.attributes].forEach((attr) => {
      if (
        attr.name.startsWith('data-') ||
        attr.name === 'style'
      ) {
        this.drawer.removeAttribute(attr.name);
      }
    });

    [...freshDrawer.attributes].forEach((attr) => {
      if (
        attr.name.startsWith('data-') ||
        attr.name === 'style'
      ) {
        this.drawer.setAttribute(
          attr.name,
          attr.value
        );
      }
    });

    this.drawer.innerHTML =
      freshDrawer.innerHTML;

    this.drawer.className =
      preservedClass;

    this.drawer.setAttribute(
      'aria-hidden',
      wasOpen ? 'false' : 'true'
    );

    this.cacheNodes();
  }

  async removeOrphanProtection() {
    const merchandiseCount = Number(
      this.drawer.dataset.cartMerchandiseCount || 0
    );

    const protectionKey =
      this.drawer.dataset.cartProtectionKey || '';

    if (
      merchandiseCount > 0 ||
      !protectionKey
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${this.rootUrl}cart/change.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: protectionKey,
            quantity: 0
          })
        }
      );

      if (response.ok) {
        await this.refresh();
      }

    } catch (error) {
      console.error(error);
    }
  }

  showSkeleton() {
    if (!this.body) return;

    const count = Math.min(
      Math.max(
        Number(
          this.drawer.dataset.cartMerchandiseCount || 2
        ),
        1
      ),
      3
    );

    const items = Array.from(
      { length: count },
      () => `
        <div class="cart-drawer__skeleton-item" aria-hidden="true">
          <div class="cart-drawer__skeleton-media"></div>

          <div class="cart-drawer__skeleton-lines">
            <div class="cart-drawer__skeleton-line"></div>
            <div class="cart-drawer__skeleton-line"></div>
            <div class="cart-drawer__skeleton-line"></div>
          </div>
        </div>
      `
    ).join('');

    this.body.innerHTML = `
      <div
        class="cart-drawer__skeleton"
        aria-busy="true"
        aria-label="${this.escapeHtml(this.loadingLabel)}"
      >
        ${items}
      </div>
    `;
  }

  updateGlobalCount() {
    const count = Number(
      this.drawer.dataset.cartItemCount || 0
    );

    this.countNodes = [
      ...document.querySelectorAll(
        '[data-cart-count]'
      )
    ];

    this.countNodes.forEach((node) => {
      node.textContent = count;
      node.hidden = count === 0;
    });
  }

  initTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    const timer =
      this.drawer.querySelector(
        '[data-cart-drawer-timer]'
      );

    if (!timer) {
      sessionStorage.removeItem(
        'custom-cart-drawer-expiry'
      );
      return;
    }

    const valueNode =
      timer.querySelector(
        '[data-cart-drawer-timer-value]'
      );

    const minutes = Math.max(
      1,
      Number(timer.dataset.minutes || 10)
    );

    const duration =
      minutes * 60 * 1000;

    const storageKey =
      'custom-cart-drawer-expiry';

    let expiry = Number(
      sessionStorage.getItem(storageKey) || 0
    );

    if (
      !expiry ||
      expiry <= Date.now()
    ) {
      expiry =
        Date.now() + duration;

      sessionStorage.setItem(
        storageKey,
        String(expiry)
      );
    }

    const tick = () => {
      const remainingMs = Math.max(
        0,
        expiry - Date.now()
      );

      const totalSeconds =
        Math.ceil(
          remainingMs / 1000
        );

      const mins =
        Math.floor(
          totalSeconds / 60
        );

      const secs =
        totalSeconds % 60;

      if (valueNode) {
        valueNode.textContent =
          `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      }

      if (
        remainingMs <= 0 &&
        this.timerInterval
      ) {
        clearInterval(
          this.timerInterval
        );

        this.timerInterval = null;
      }
    };

    tick();

    this.timerInterval =
      window.setInterval(
        tick,
        1000
      );
  }

  handleKeydown(event) {
    if (
      !this.drawer.classList.contains(
        'is-open'
      )
    ) {
      return;
    }

    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  open() {
    this.drawer.classList.add(
      'is-open'
    );

    this.drawer.setAttribute(
      'aria-hidden',
      'false'
    );

    document.body.classList.add(
      'cart-drawer-open'
    );

    this.toggleNodes.forEach(
      (node) =>
        node.setAttribute(
          'aria-expanded',
          'true'
        )
    );

    this.setBackgroundInert(true);

    const focusTarget =
      this.panel?.querySelector(
        '#CartDrawerTitle'
      ) ||
      this.panel?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

    focusTarget?.focus({
      preventScroll: true
    });
  }

  close() {
    this.drawer.classList.remove(
      'is-open'
    );

    this.drawer.setAttribute(
      'aria-hidden',
      'true'
    );

    document.body.classList.remove(
      'cart-drawer-open'
    );

    this.toggleNodes.forEach(
      (node) =>
        node.setAttribute(
          'aria-expanded',
          'false'
        )
    );

    this.setBackgroundInert(false);

    this.lastActiveElement?.focus();
  }

  setBackgroundInert(isInert) {
    if (isInert) {
      this.inertedElements = [
        ...document.body.children
      ].filter(
        (el) =>
          el !== this.drawer &&
          el.tagName !== 'SCRIPT' &&
          !el.hasAttribute('inert')
      );

      this.inertedElements.forEach(
        (el) =>
          el.setAttribute(
            'inert',
            ''
          )
      );

    } else {
      (
        this.inertedElements || []
      ).forEach(
        (el) =>
          el.removeAttribute(
            'inert'
          )
      );

      this.inertedElements = [];
    }
  }

  setItemPendingState(key, isPending) {
    const item =
      this.body?.querySelector(
        `[data-cart-item-key="${CSS.escape(key)}"]`
      );

    if (!item) return;

    item.classList.toggle(
      'is-pending',
      isPending
    );

    item.querySelectorAll(
      'button, input'
    ).forEach((element) => {
      element.disabled =
        isPending;
    });
  }

  trapFocus(event) {
    const focusable = [
      ...(
        this.panel?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) || []
      )
    ].filter(
      (node) =>
        !node.hasAttribute(
          'disabled'
        )
    );

    if (!focusable.length) return;

    const first =
      focusable[0];

    const last =
      focusable[
        focusable.length - 1
      ];

    if (
      event.shiftKey &&
      document.activeElement === first
    ) {
      event.preventDefault();
      last.focus();

    } else if (
      !event.shiftKey &&
      document.activeElement === last
    ) {
      event.preventDefault();
      first.focus();
    }
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

document.addEventListener(
  'DOMContentLoaded',
  () => {
    window.customCartDrawer =
      new CartDrawerController();
  }
);